import React, { useState } from 'react';
import { Container, Stack, Alert, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Page } from '../../../../components';
import HeaderBreadcrumbs from '../../../../components/header-breadcrumbs';
import { permissionCheck } from '../../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../../constants/systemPermissions.constants';
import AreYouSureModal from '../../../../components/are-you-sure-modal';
import { bulkActionsApi } from '../../../../services/bulk-actions/bulkActionsApi';
import { handleApiError } from '../../../../services/react-query/react-query.client';
import { BulkDeletePreviewResponse, BulkDeleteCommitResponse } from '../../../../types/bulkActions.types';
import { useEffectiveSettings, SETTING } from '../../../../hooks/use-effective-settings';

import { UploadStep } from './UploadStep';
import { PreviewStep } from './PreviewStep';
import { ResultStep } from './ResultStep';
import { formatTimeEstimate } from './timeEstimate';

type Step = 'upload' | 'preview' | 'result';

const getConfirmDescription = (count: number, isCommitting: boolean): string => {
    const userLabel = `${count} user${count !== 1 ? 's' : ''}`;
    const estimate = formatTimeEstimate(count);

    if (isCommitting) {
        return estimate
            ? `Deleting ${userLabel}. Estimated time: ${estimate}. Please do not close this page.`
            : `Deleting ${userLabel}. Please do not close this page.`;
    }

    return estimate
        ? `You are about to delete ${userLabel}. Estimated time: ${estimate}. Are you sure you want to proceed?`
        : `You are about to delete ${userLabel}. Are you sure you want to proceed?`;
};

export const BulkDeleteUsersScreen: React.FC = () => {
    const canAccess = permissionCheck(customerPermissionFlags.SUPER_ADMIN);

    // Customer settings for unique identifier
    const { getSetting, isLoading: isSettingsLoading } = useEffectiveSettings();
    const userUniqueIdentifier = getSetting(SETTING.userUniqueIdentifier, '');

    // Step flow state
    const [step, setStep] = useState<Step>('upload');
    const [attemptId, setAttemptId] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<BulkDeletePreviewResponse | null>(null);
    const [commitResult, setCommitResult] = useState<BulkDeleteCommitResponse | null>(null);
    const [acknowledgedMismatches, setAcknowledgedMismatches] = useState(false);

    // Confirm modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isCommitting, setIsCommitting] = useState(false);

    if (!canAccess) {
        return <Navigate to="/" replace />;
    }

    const resetFlow = () => {
        setStep('upload');
        setAttemptId(null);
        setPreviewData(null);
        setCommitResult(null);
        setAcknowledgedMismatches(false);
        setShowConfirmModal(false);
        setIsCommitting(false);
    };

    const handlePreviewComplete = (data: BulkDeletePreviewResponse) => {
        setPreviewData(data);
        setAttemptId(data.attemptId);
        setAcknowledgedMismatches(false);
        setStep('preview');
    };

    const handleContinue = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!attemptId) return;

        setIsCommitting(true);
        try {
            const result = await bulkActionsApi.deleteUsersCommit(attemptId, acknowledgedMismatches);
            setCommitResult(result);
            setShowConfirmModal(false);
            setStep('result');
        } catch (err: any) {
            const msg = handleApiError(err);

            // Check for expired preview
            if (err?.response?.status === 410 || msg.includes('PREVIEW_EXPIRED')) {
                toast.error('Preview has expired. Please start over with a new upload.');
                resetFlow();
            } else {
                toast.error(`Delete failed: ${msg}`);
            }

            setShowConfirmModal(false);
        } finally {
            setIsCommitting(false);
        }
    };

    const totalMatchedCount = previewData
        ? previewData.summary.matchedCleanCount + previewData.summary.matchedWithMismatchCount
        : 0;

    const renderStep = () => {
        switch (step) {
            case 'upload':
                return (
                    <UploadStep
                        identifierField={userUniqueIdentifier || 'INVITECODE'}
                        isSettingsLoading={isSettingsLoading}
                        onPreviewComplete={handlePreviewComplete}
                    />
                );
            case 'preview':
                if (!previewData) return null;
                return (
                    <PreviewStep
                        previewData={previewData}
                        acknowledgedMismatches={acknowledgedMismatches}
                        onAcknowledgedMismatchesChange={setAcknowledgedMismatches}
                        onContinue={handleContinue}
                        onBack={resetFlow}
                    />
                );
            case 'result':
                if (!commitResult || !attemptId) return null;
                return (
                    <ResultStep
                        commitResult={commitResult}
                        attemptId={attemptId}
                        onStartNew={resetFlow}
                    />
                );
        }
    };

    return (
        <Page title="POM - Bulk Delete Users">
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <HeaderBreadcrumbs
                        heading="Bulk Delete Users"
                        links={[
                            { name: 'Bulk Actions', href: '/bulk-actions' },
                            { name: 'Delete Users', href: '/bulk-actions/delete-users' },
                        ]}
                        activeLast
                    />
                </Stack>

                {renderStep()}

                {/* Confirm delete modal */}
                <AreYouSureModal
                    visible={showConfirmModal}
                    loading={isCommitting}
                    title="Confirm Bulk Delete"
                    description={getConfirmDescription(totalMatchedCount, isCommitting)}
                    color="error"
                    onConfirm={handleConfirmDelete}
                    onHide={() => !isCommitting && setShowConfirmModal(false)}
                />
            </Container>
        </Page>
    );
};
